import React, { useState, useEffect, useRef } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { 
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter 
} from '../../components/ui/dialog';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Loader2, Plus, Search, Edit2, Trash2, Upload, Download, BookOpen,
  X, Sparkles, Filter, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import StatsSummaryBar from '../../components/StatsSummaryBar';
import ReferencesSection from '../../components/ReferencesSection';

import { API_BASE } from '../../config/api';
const API = `${API_BASE}`;

const PARTS_OF_SPEECH = [
  'noun', 'verb', 'adjective', 'adverb', 'pronoun', 
  'preposition', 'conjunction', 'interjection', 'determiner', 'particle'
];

const LexiconPage = () => {
  const { conlang, fetchConlang , canEdit} = useOutletContext();
  const { conlangId } = useParams();
  
  const [words, setWords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPOS, setFilterPOS] = useState('');
  
  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [generatorDialogOpen, setGeneratorDialogOpen] = useState(false);
  const [selectedWord, setSelectedWord] = useState(null);
  
  // Form states
  const [formWord, setFormWord] = useState('');
  const [formIPA, setFormIPA] = useState('');
  const [formPOS, setFormPOS] = useState('');
  const [formDefinition, setFormDefinition] = useState('');
  const [formEtymology, setFormEtymology] = useState('');
  const [formExamples, setFormExamples] = useState('');
  const [formTags, setFormTags] = useState('');
  const [formNotes, setFormNotes] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  
  // Import states
  const [importText, setImportText] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [importRefUrl, setImportRefUrl] = useState('');
  const [importRefLabel, setImportRefLabel] = useState('');
  
  // Generator states
  const [genCount, setGenCount] = useState(10);
  const [genMinSyllables, setGenMinSyllables] = useState(1);
  const [genMaxSyllables, setGenMaxSyllables] = useState(3);
  const [generatedWords, setGeneratedWords] = useState([]);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchWords();
  }, [conlangId]);

  const fetchWords = async () => {
    try {
      const response = await axios.get(`${API}/conlangs/${conlangId}/words`, {
        withCredentials: true
      });
      setWords(response.data);
    } catch (error) {
      toast.error('Failed to load lexicon');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormWord('');
    setFormIPA('');
    setFormPOS('');
    setFormDefinition('');
    setFormEtymology('');
    setFormExamples('');
    setFormTags('');
    setFormNotes('');
    setSelectedWord(null);
  };

  const handleAddWord = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    
    try {
      const response = await axios.post(`${API}/conlangs/${conlangId}/words`, {
        word: formWord,
        ipa: formIPA || null,
        part_of_speech: formPOS || null,
        definition: formDefinition,
        etymology: formEtymology || null,
        example_sentences: formExamples ? formExamples.split('\n').filter(s => s.trim()) : [],
        tags: formTags ? formTags.split(',').map(t => t.trim()).filter(t => t) : [],
        notes: formNotes || null,
        references: selectedWord?.references || []
      }, { withCredentials: true });
      
      setWords([...words, response.data]);
      setAddDialogOpen(false);
      resetForm();
      toast.success('Word added to lexicon');
      fetchConlang();
    } catch (error) {
      toast.error('Failed to add word');
    } finally {
      setFormLoading(false);
    }
  };

  const handleEditWord = async (e) => {
    e.preventDefault();
    if (!selectedWord) return;
    setFormLoading(true);
    
    try {
      const response = await axios.put(`${API}/conlangs/${conlangId}/words/${selectedWord.word_id}`, {
        word: formWord,
        ipa: formIPA || null,
        part_of_speech: formPOS || null,
        definition: formDefinition,
        etymology: formEtymology || null,
        example_sentences: formExamples ? formExamples.split('\n').filter(s => s.trim()) : [],
        tags: formTags ? formTags.split(',').map(t => t.trim()).filter(t => t) : [],
        notes: formNotes || null,
        references: selectedWord?.references || []
      }, { withCredentials: true });
      
      setWords(words.map(w => w.word_id === selectedWord.word_id ? response.data : w));
      setEditDialogOpen(false);
      resetForm();
      toast.success('Word updated');
    } catch (error) {
      toast.error('Failed to update word');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteWord = async (wordId) => {
    try {
      await axios.delete(`${API}/conlangs/${conlangId}/words/${wordId}`, {
        withCredentials: true
      });
      setWords(words.filter(w => w.word_id !== wordId));
      toast.success('Word deleted');
      fetchConlang();
    } catch (error) {
      toast.error('Failed to delete word');
    }
  };

  const openEditDialog = (word) => {
    setSelectedWord(word);
    setFormWord(word.word);
    setFormIPA(word.ipa || '');
    setFormPOS(word.part_of_speech || '');
    setFormDefinition(word.definition);
    setFormEtymology(word.etymology || '');
    setFormExamples(word.example_sentences?.join('\n') || '');
    setFormTags(word.tags?.join(', ') || '');
    setFormNotes(word.notes || '');
    setEditDialogOpen(true);
  };

  const handleImport = async () => {
    setImportLoading(true);
    try {
      // Parse import text (JSON or simple format)
      let wordsToImport = [];
      
      try {
        // Try JSON first
        wordsToImport = JSON.parse(importText);
        if (!Array.isArray(wordsToImport)) {
          wordsToImport = [wordsToImport];
        }
      } catch {
        // Try simple format: word | definition (one per line)
        const lines = importText.split('\n').filter(l => l.trim());
        wordsToImport = lines.map(line => {
          const parts = line.split('|').map(p => p.trim());
          return {
            word: parts[0],
            definition: parts[1] || 'No definition',
            part_of_speech: parts[2] || null,
            ipa: parts[3] || null
          };
        });
      }
      
      const importPayload = { words: wordsToImport };
      if (importRefUrl || importRefLabel) {
        importPayload.reference = {};
        if (importRefUrl) importPayload.reference.url = importRefUrl;
        if (importRefLabel) importPayload.reference.label = importRefLabel;
      }

      const response = await axios.post(`${API}/conlangs/${conlangId}/words/bulk`, importPayload, { withCredentials: true });
      
      toast.success(`Imported ${response.data.imported} words (${response.data.skipped} skipped)`);
      setImportDialogOpen(false);
      setImportText('');
      setImportRefUrl('');
      setImportRefLabel('');
      fetchWords();
      fetchConlang();
    } catch (error) {
      toast.error('Failed to import words. Check format.');
    } finally {
      setImportLoading(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/conlangs/${conlangId}/generate-words`, {
        count: genCount,
        min_syllables: genMinSyllables,
        max_syllables: genMaxSyllables
      }, { withCredentials: true });
      setGeneratedWords(response.data);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to generate words';
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  const addGeneratedWord = async (genWord) => {
    try {
      const response = await axios.post(`${API}/conlangs/${conlangId}/words`, {
        word: genWord.word,
        ipa: genWord.ipa,
        definition: 'Definition needed',
        notes: `Generated. Syllables: ${genWord.syllables.join('-')}`
      }, { withCredentials: true });
      
      setWords([...words, response.data]);
      setGeneratedWords(generatedWords.filter(w => w.word !== genWord.word));
      toast.success(`Added "${genWord.word}" to lexicon`);
      fetchConlang();
    } catch (error) {
      toast.error('Failed to add word');
    }
  };

  const exportLexicon = () => {
    const exportData = words.map(w => ({
      word: w.word,
      ipa: w.ipa,
      part_of_speech: w.part_of_speech,
      definition: w.definition,
      etymology: w.etymology,
      example_sentences: w.example_sentences,
      tags: w.tags,
      notes: w.notes
    }));
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${conlang?.name || 'lexicon'}_lexicon.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredWords = words.filter(w => {
    const matchesSearch = !searchQuery || 
      w.word.toLowerCase().includes(searchQuery.toLowerCase()) ||
      w.definition.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPOS = !filterPOS || filterPOS === 'all' || w.part_of_speech === filterPOS;
    return matchesSearch && matchesPOS;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl space-y-6">
      {/* Stats Summary Bar */}
      <StatsSummaryBar conlang={conlang} conlangId={conlangId} currentSection="lexicon" />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-heading text-3xl font-bold text-foreground" data-testid="lexicon-title">
            Lexicon
          </h2>
          <p className="text-foreground-muted mt-1">
            {words.length} {words.length === 1 ? 'word' : 'words'} in {conlang?.name}
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          {canEdit && (
          <>
          <Button 
            variant="outline" 
            className="border-border"
            onClick={() => setGeneratorDialogOpen(true)}
            data-testid="generator-btn"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Generate
          </Button>
          <Button 
            variant="outline" 
            className="border-border"
            onClick={() => setImportDialogOpen(true)}
            data-testid="import-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Import
          </Button>
          </>
          )}
          <Button 
            variant="outline" 
            className="border-border"
            onClick={exportLexicon}
            disabled={words.length === 0}
            data-testid="export-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          {canEdit && (
          <Button 
            onClick={() => setAddDialogOpen(true)}
            className="bg-primary hover:bg-primary/90"
            data-testid="add-word-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Word
          </Button>
          )}
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-foreground-subtle" />
          <Input
            placeholder="Search words or definitions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-background-paper border-border"
            data-testid="search-input"
          />
        </div>
        <Select value={filterPOS} onValueChange={setFilterPOS}>
          <SelectTrigger className="w-[180px] bg-background-paper border-border" data-testid="filter-pos">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="All parts of speech" />
          </SelectTrigger>
          <SelectContent className="bg-background-paper border-border">
            <SelectItem value="all">All parts of speech</SelectItem>
            {PARTS_OF_SPEECH.map(pos => (
              <SelectItem key={pos} value={pos} className="capitalize">{pos}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Word List */}
      {filteredWords.length > 0 ? (
        <div className="space-y-2" data-testid="words-list">
          {filteredWords.map(word => (
            <Card 
              key={word.word_id} 
              className="bg-background-paper border-border hover:border-primary/30 transition-colors"
              data-testid={`word-${word.word_id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="font-heading text-xl text-foreground font-semibold">
                        {word.word}
                      </span>
                      {word.ipa && (
                        <span className="font-conlang text-secondary text-sm">{word.ipa}</span>
                      )}
                      {word.part_of_speech && (
                        <Badge variant="outline" className="border-primary/50 text-primary text-xs">
                          {word.part_of_speech}
                        </Badge>
                      )}
                    </div>
                    <p className="text-foreground-muted">{word.definition}</p>
                    {word.tags?.length > 0 && (
                      <div className="flex gap-1 mt-2">
                        {word.tags.map(tag => (
                          <Badge key={tag} variant="secondary" className="bg-accent text-foreground-muted text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    )}
                    {word.references?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {word.references.map((ref, i) => (
                          <span key={i} className="text-xs text-foreground-subtle inline-flex items-center gap-1">
                            {ref.url ? (
                              <a href={ref.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-0.5">
                                <BookOpen className="w-3 h-3" />{ref.label || ref.url}<ExternalLink className="w-2.5 h-2.5" />
                              </a>
                            ) : (
                              <span className="inline-flex items-center gap-0.5"><BookOpen className="w-3 h-3" />{ref.label}</span>
                            )}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  {canEdit && (
                  <div className="flex items-center gap-1">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => openEditDialog(word)}
                      data-testid={`edit-word-${word.word_id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleDeleteWord(word.word_id)}
                      className="text-destructive hover:text-destructive"
                      data-testid={`delete-word-${word.word_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : words.length === 0 ? (
        <Card className="bg-background-paper border-border border-dashed">
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-tertiary/20 flex items-center justify-center mx-auto mb-4">
              <BookOpen className="w-8 h-8 text-tertiary" />
            </div>
            <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
              No words yet
            </h3>
            <p className="text-foreground-muted mb-6 max-w-md mx-auto">
              Start building your vocabulary. Add words manually, import from a file, 
              or use the generator to create words based on your phonology.
            </p>
            <div className="flex justify-center gap-3">
              <Button onClick={() => setGeneratorDialogOpen(true)} variant="outline" className="border-border">
                <Sparkles className="w-4 h-4 mr-2" />
                Generate Words
              </Button>
              <Button onClick={() => setAddDialogOpen(true)} className="bg-primary hover:bg-primary/90">
                <Plus className="w-4 h-4 mr-2" />
                Add Word
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <p className="text-center text-foreground-muted py-8">No words match your search</p>
      )}

      {/* Add Word Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="bg-background-paper border-border max-w-lg max-h-[90vh] overflow-y-auto" data-testid="add-word-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-foreground">Add Word</DialogTitle>
            <DialogDescription className="text-foreground-muted">
              Add a new word to your lexicon
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddWord} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-foreground">Word *</Label>
                <Input
                  value={formWord}
                  onChange={(e) => setFormWord(e.target.value)}
                  className="bg-background border-border font-mono"
                  required
                  data-testid="form-word"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">IPA Pronunciation</Label>
                <Input
                  value={formIPA}
                  onChange={(e) => setFormIPA(e.target.value)}
                  placeholder="/.../"
                  className="bg-background border-border font-mono"
                  data-testid="form-ipa"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-foreground">Part of Speech</Label>
              <Select value={formPOS} onValueChange={setFormPOS}>
                <SelectTrigger className="bg-background border-border" data-testid="form-pos">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent className="bg-background-paper border-border">
                  {PARTS_OF_SPEECH.map(pos => (
                    <SelectItem key={pos} value={pos} className="capitalize">{pos}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-foreground">Definition *</Label>
              <Textarea
                value={formDefinition}
                onChange={(e) => setFormDefinition(e.target.value)}
                className="bg-background border-border min-h-[80px]"
                required
                data-testid="form-definition"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-foreground">Etymology</Label>
              <Input
                value={formEtymology}
                onChange={(e) => setFormEtymology(e.target.value)}
                placeholder="Word origin..."
                className="bg-background border-border"
                data-testid="form-etymology"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-foreground">Example Sentences (one per line)</Label>
              <Textarea
                value={formExamples}
                onChange={(e) => setFormExamples(e.target.value)}
                placeholder="Example usage..."
                className="bg-background border-border min-h-[60px]"
                data-testid="form-examples"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-foreground">Tags (comma-separated)</Label>
              <Input
                value={formTags}
                onChange={(e) => setFormTags(e.target.value)}
                placeholder="nature, animal, common..."
                className="bg-background border-border"
                data-testid="form-tags"
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setAddDialogOpen(false); resetForm(); }} className="border-border">
                Cancel
              </Button>
              <Button type="submit" disabled={formLoading || !formWord || !formDefinition} className="bg-primary hover:bg-primary/90" data-testid="submit-word">
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Word'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Word Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-background-paper border-border max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-word-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-foreground">Edit Word</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditWord} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-foreground">Word *</Label>
                <Input
                  value={formWord}
                  onChange={(e) => setFormWord(e.target.value)}
                  className="bg-background border-border font-mono"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">IPA</Label>
                <Input
                  value={formIPA}
                  onChange={(e) => setFormIPA(e.target.value)}
                  className="bg-background border-border font-mono"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-foreground">Part of Speech</Label>
              <Select value={formPOS} onValueChange={setFormPOS}>
                <SelectTrigger className="bg-background border-border">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent className="bg-background-paper border-border">
                  {PARTS_OF_SPEECH.map(pos => (
                    <SelectItem key={pos} value={pos} className="capitalize">{pos}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-foreground">Definition *</Label>
              <Textarea
                value={formDefinition}
                onChange={(e) => setFormDefinition(e.target.value)}
                className="bg-background border-border min-h-[80px]"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-foreground">Etymology</Label>
              <Input
                value={formEtymology}
                onChange={(e) => setFormEtymology(e.target.value)}
                className="bg-background border-border"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-foreground">Example Sentences</Label>
              <Textarea
                value={formExamples}
                onChange={(e) => setFormExamples(e.target.value)}
                className="bg-background border-border min-h-[60px]"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-foreground">Tags</Label>
              <Input
                value={formTags}
                onChange={(e) => setFormTags(e.target.value)}
                className="bg-background border-border"
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setEditDialogOpen(false); resetForm(); }} className="border-border">
                Cancel
              </Button>
              <Button type="submit" disabled={formLoading} className="bg-primary hover:bg-primary/90">
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="bg-background-paper border-border max-w-lg" data-testid="import-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-foreground">Import Words</DialogTitle>
            <DialogDescription className="text-foreground-muted">
              Paste JSON or simple format (word | definition | POS | IPA)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
              placeholder={`JSON format:
[{"word": "katal", "definition": "ocean", "part_of_speech": "noun"}]

Or simple format (one per line):
katal | ocean | noun | /katal/
mira | to see | verb`}
              className="bg-background border-border min-h-[200px] font-mono text-sm"
              data-testid="import-textarea"
            />
            <div className="space-y-2 border-t border-border pt-3">
              <Label className="text-foreground text-sm font-semibold">Batch Reference <span className="text-foreground-muted font-normal">(applied to all imported words)</span></Label>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  value={importRefUrl}
                  onChange={(e) => setImportRefUrl(e.target.value)}
                  placeholder="URL (optional)"
                  className="bg-background border-border text-sm"
                  data-testid="import-ref-url"
                />
                <Input
                  value={importRefLabel}
                  onChange={(e) => setImportRefLabel(e.target.value)}
                  placeholder="Description (optional)"
                  className="bg-background border-border text-sm"
                  data-testid="import-ref-label"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setImportDialogOpen(false)} className="border-border">
                Cancel
              </Button>
              <Button onClick={handleImport} disabled={importLoading || !importText.trim()} className="bg-primary hover:bg-primary/90" data-testid="import-submit">
                {importLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Import'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Word Generator Dialog */}
      <Dialog open={generatorDialogOpen} onOpenChange={setGeneratorDialogOpen}>
        <DialogContent className="bg-background-paper border-border max-w-lg max-h-[90vh] overflow-y-auto" data-testid="generator-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-foreground flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-tertiary" />
              Word Generator
            </DialogTitle>
            <DialogDescription className="text-foreground-muted">
              Generate words based on your phonology and phonotactics
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-foreground">Count</Label>
                <Input
                  type="number"
                  min={1}
                  max={50}
                  value={genCount}
                  onChange={(e) => setGenCount(parseInt(e.target.value) || 10)}
                  className="bg-background border-border"
                  data-testid="gen-count"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">Min Syllables</Label>
                <Input
                  type="number"
                  min={1}
                  max={5}
                  value={genMinSyllables}
                  onChange={(e) => setGenMinSyllables(parseInt(e.target.value) || 1)}
                  className="bg-background border-border"
                  data-testid="gen-min"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">Max Syllables</Label>
                <Input
                  type="number"
                  min={1}
                  max={6}
                  value={genMaxSyllables}
                  onChange={(e) => setGenMaxSyllables(parseInt(e.target.value) || 3)}
                  className="bg-background border-border"
                  data-testid="gen-max"
                />
              </div>
            </div>
            
            <Button onClick={handleGenerate} disabled={generating} className="w-full bg-tertiary hover:bg-tertiary/90 text-black" data-testid="generate-btn">
              {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Generate Words
            </Button>
            
            {generatedWords.length > 0 && (
              <div className="space-y-2">
                <Label className="text-foreground">Generated Words (click to add)</Label>
                <div className="max-h-[200px] overflow-y-auto space-y-1" data-testid="generated-words">
                  {generatedWords.map((gw, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center justify-between p-2 rounded bg-background hover:bg-accent cursor-pointer group"
                      onClick={() => addGeneratedWord(gw)}
                    >
                      <div>
                        <span className="font-conlang text-foreground">{gw.word}</span>
                        <span className="font-conlang text-secondary text-sm ml-2">{gw.ipa}</span>
                      </div>
                      <Button size="sm" variant="ghost" className="opacity-0 group-hover:opacity-100">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      <ReferencesSection section="lexicon" />
    </div>
  );
};

export default LexiconPage;
